'use client';

import { FC } from 'react';
import { useCurrencyStore } from '@/store/currencyStore';
import { useConvertedPrice } from '@/hooks/useCurrencyPrice';
import { getCurrencyCode, BackendCurrency } from '@/utils/currencyHelpers';

interface CurrencyDisplayProps {
  /** The amount from backend */
  amount: number;
  /** Backend's currency - can be string ('USD') or object ({ code: 'USD' }). Defaults to 'USD' if not provided */
  backendCurrency?: BackendCurrency;
  /** Optional conversion rate provided by backend (e.g. USD→INR) */
  backendConversionRate?: number;
  /** Optional className for styling the entire display */
  className?: string;
  /** Optional className for styling just the symbol */
  symbolClassName?: string;
  /** Optional className for styling just the amount */
  amountClassName?: string;
  /** Whether to show decimal places (default: true) */
  showDecimals?: boolean;
  /** Number of decimal places (default: 2) */
  decimalPlaces?: number;
  /** Font size for the amount (default: '16px') */
  fontSize?: string;
}

/**
 * CurrencyDisplay Component
 *
 * Intelligently displays prices converted to user's currency based on their IP location.
 *
 * **Smart Conversion Logic:**
 * - If backend currency === user currency → No conversion (show as-is)
 * - If backend currency !== user currency → Convert using exchange rate
 *
 * **Examples:**
 *
 * India User (wants INR):
 * - Backend USD $100 → Converts → ₹8350
 * - Backend INR ₹100 → No conversion → ₹100
 *
 * USA User (wants USD):
 * - Backend USD $100 → No conversion → $100
 * - Backend INR ₹8350 → Converts → $100
 *
 * Usage:
 * ```tsx
 * <CurrencyDisplay amount={100} />  // Assumes backend is USD
 * <CurrencyDisplay amount={100} backendCurrency="INR" />  // Explicit INR
 * <CurrencyDisplay amount={100} backendCurrency="USD" />  // Explicit USD
 * ```
 */
const CurrencyDisplay: FC<CurrencyDisplayProps> = ({
  amount,
  backendCurrency, // Can be string, object, or undefined
  backendConversionRate,
  className = '',
  symbolClassName = '',
  amountClassName = '',
  showDecimals = true,
  decimalPlaces = 2,
  fontSize = '16px',
}) => {
  const { currencySymbol, currencyCode } = useCurrencyStore();

  // Safely extract currency code (handles string/object/undefined)
  const backendCurrencyCode = getCurrencyCode(backendCurrency);

  // Handle undefined or invalid amount
  const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;

  // Always call hook (hooks must be called unconditionally)
  const convertedAmount = useConvertedPrice(
    safeAmount,
    backendCurrencyCode,
    backendConversionRate
  );

  const finalAmount =
    backendCurrencyCode !== currencyCode ? convertedAmount : safeAmount;

  // Ensure finalAmount is a valid number before formatting
  const validFinalAmount =
    typeof finalAmount === 'number' && !isNaN(finalAmount) ? finalAmount : 0;

  // Round the amount properly: if not showing decimals, round to nearest whole number
  // Otherwise, keep the full precision for decimal calculation
  const roundedAmount = showDecimals
    ? validFinalAmount
    : Math.round(validFinalAmount);

  // Format the amount with Indian number format (commas)
  const integerPart = Math.floor(roundedAmount);
  const decimalPart = roundedAmount % 1;
  const hasDecimals = decimalPart > 0 && showDecimals;

  const formattedInteger = integerPart.toLocaleString('en-IN');
  const formattedDecimals = hasDecimals
    ? decimalPart.toFixed(decimalPlaces).substring(2)
    : '';

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '0px',
        overflow: 'hidden',
        maxWidth: '100%',
        minWidth: 0,
        fontWeight: 'bold',
        lineHeight: 'normal',
      }}
    >
      {/* Currency Symbol - uses fontSize prop to match amount */}
      <span
        className={symbolClassName}
        style={{
          fontSize: fontSize,
          flexShrink: 0,
          overflow: 'visible',
          lineHeight: '1',
        }}
      >
        {currencySymbol}
      </span>
      {/* Integer Part - uses fontSize prop */}
      <span
        className={amountClassName}
        style={{
          fontSize: fontSize,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flexShrink: 0,
          lineHeight: '1',
        }}
      >
        {formattedInteger}
      </span>
      {/* Decimal Point - uses fontSize prop */}
      {hasDecimals && (
        <span
          style={{
            fontSize: fontSize,
            flexShrink: 0,
          }}
        >
          .
        </span>
      )}
      {/* Decimal Digits - 12px per Figma */}
      {hasDecimals && (
        <span
          style={{
            fontSize: '12px',
            flexShrink: 0,
          }}
        >
          {formattedDecimals}
        </span>
      )}
    </span>
  );
};

export default CurrencyDisplay;
